import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { catchError, map, Observable, of } from 'rxjs';

import { ApiDataType, EnvironmentInterface } from '../../../../types';
import { ErrorDisplayService } from '../../../error';
import { UnitResult } from '../../../error/Domain/unit-result';
import { ApiClient } from '../../Domain/Adapter/api.client';
import { DavidResponseProcessor } from './david-response.processor';
import { LaravelErrorMapperService } from './laravel-error-mapper.service';

/**
 * Represents a record of parameters for a GET request, where each key is a string and its corresponding value
 * can be a string, number, or boolean. This type is typically used to construct query parameters for URLs
 * in web applications or APIs.
 */
type GetParams = Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;

/**
 * Represents the context of an error that occurred during an HTTP request.
 *
 * This type is used to encapsulate details related to the HTTP request and its associated error response.
 *
 * Properties:
 * - url: The endpoint URL where the HTTP request was made.
 * - requestData: The data payload sent with the HTTP request, represented as a key-value pair object.
 * - errorResponse: The error response received from the server, containing details of the HTTP error.
 */
export type ErrorContext = {
    url: string;
    requestData: Record<string, unknown>;
    errorResponse: HttpErrorResponse;
};

/**
 * LaravelApiClientService
 *
 * Angular service for communicating with a Laravel backend API.
 *
 * Extends the ApiClient abstract class and implements the `get`, `post`, and `put` methods using Angular's HttpClient.
 * Automatically constructs the full API URL from the injected environment configuration.
 * Uses LaravelErrorMapperService to handle Laravel-style error responses.
 *
 * Responsibilities:
 * - Makes HTTP GET, POST, and PUT requests to the Laravel backend API.
 * - Handles error responses using LaravelErrorMapperService.
 * - Displays error modals to the user when errors occur (if displayErrors is true).
 * - Returns typed responses wrapped in UnitResult objects for consistent error handling.
 *
 * Dependencies:
 * - HttpClient: Angular's HTTP client for making requests.
 * - environment: Injected environment configuration containing the base API URL.
 * - LaravelErrorMapperService: Service for mapping Laravel API errors to UnitResult objects.
 * - ErrorService: Service for displaying error modals to the user.
 *
 * Usage:
 *   Inject this service where API communication is needed. Use the `get`, `post`, or `put` methods
 *   to send HTTP requests and receive typed responses as RxJS Observables.
 */

@Injectable({
    providedIn: 'root'
})
export class LaravelApiClientService extends ApiClient {
    constructor(
        @Inject('environment') private readonly environment: EnvironmentInterface,
        private readonly http: HttpClient,
        private readonly errorMapper: LaravelErrorMapperService,
        private readonly errorService: ErrorDisplayService,
        private readonly davidResponseProcessor: DavidResponseProcessor
    ) {
        super();
    }

    /**
     * Makes a PUT request to the specified endpoint with the provided data.
     *
     * This method:
     * 1. Constructs the full URL by combining the base API URL with the endpoint.
     * 2. Sends the request using Angular's HttpClient.
     * 3. Maps successful responses to UnitResult.success objects.
     * 4. Catches and maps errors using LaravelErrorMapperService.
     * 5. Optionally displays error modals to the user.
     *
     * @template Request - The type of the request payload.
     * @template Response - The type of the expected response.
     * @param endpoint - The API endpoint to send the request to (without the base URL).
     * @param data - The request payload to send.
     * @param displayErrors - Whether to display error modals to the user.
     * @returns An Observable of UnitResult<Response> that will emit either:
     *          - A success result containing the response data, or
     *          - A failure result containing error information.
     */
    put<Request, Response>(endpoint: string, data: Request, displayErrors: boolean): Observable<UnitResult<Response>> {
        const url = `${this.environment.apiUrl}${endpoint}`;
        return this.http.put<Response>(url, data).pipe(
            map(response => this.mapSuccessResponse<Response>(response)),
            catchError(error => {
                const context: ErrorContext = {
                    url: url,
                    requestData: data as Record<string, unknown>,
                    errorResponse: error
                };

                return this.catchError<Response>(context, displayErrors);
            })
        );
    }

    /**
     * Makes a PATCH request to the specified endpoint with the provided data.
     *
     * This method:
     * 1. Constructs the full URL by combining the base API URL with the endpoint.
     * 2. Sends the request using Angular's HttpClient.
     * 3. Returns a success UnitResult with null value on successful response.
     * 4. Catches and maps errors using LaravelErrorMapperService.
     * 5. Optionally displays error modals to the user.
     *
     * @template Request - The type of the request payload.
     * @param endpoint - The API endpoint to send the request to (without the base URL).
     * @param data - The request payload to send.
     * @param displayErrors - Whether to display error modals to the user.
     * @returns An Observable of UnitResult<null> that will emit either:
     *          - A success result with null value, or
     *          - A failure result containing error information.
     */
    patch<Request>(endpoint: string, data: Request, displayErrors: boolean): Observable<UnitResult<null>> {
        const url = `${this.environment.apiUrl}${endpoint}`;
        return this.http.patch<void>(url, data).pipe(
            map(() => UnitResult.success<null>(null)),
            catchError(error => {
                const context: ErrorContext = {
                    url: url,
                    requestData: data as Record<string, unknown>,
                    errorResponse: error
                };

                return this.catchError<null>(context, displayErrors);
            })
        );
    }

    /**
     * Makes a POST request to the specified endpoint with the provided data.
     *
     * This method:
     * 1. Constructs the full URL by combining the base API URL with the endpoint.
     * 2. Sends the request using Angular's HttpClient.
     * 3. Maps successful responses to UnitResult.success objects.
     * 4. Catches and maps errors using LaravelErrorMapperService.
     * 5. Optionally displays error modals to the user.
     *
     * @template Request - The type of the request payload.
     * @template Response - The type of the expected response.
     * @param endpoint - The API endpoint to send the request to (without the base URL).
     * @param data - The request payload to send.
     * @param displayErrors - Whether to display error modals to the user (default: true).
     * @returns An Observable of UnitResult<Response> that will emit either:
     *          - A success result containing the response data, or
     *          - A failure result containing error information.
     */
    post<Request, Response>(
        endpoint: string,
        data: Request,
        displayErrors: boolean = true
    ): Observable<UnitResult<Response>> {
        const url = `${this.environment.apiUrl}${endpoint}`;

        return this.http.post<Response>(url, data).pipe(
            map(response => this.mapSuccessResponse<Response>(response)),
            catchError(error => {
                const context: ErrorContext = {
                    url: url,
                    requestData: data as Record<string, unknown>,
                    errorResponse: error
                };

                return this.catchError<Response>(context, displayErrors);
            })
        );
    }

    /**
     * Makes a GET request to the specified endpoint.
     *
     * This method:
     * 1. Constructs the full URL by combining the base API URL with the endpoint.
     * 2. Sends the request using Angular's HttpClient.
     * 3. Maps successful responses to UnitResult.success objects.
     * 4. Catches and maps errors using LaravelErrorMapperService.
     * 5. Optionally displays error modals to the user.
     *
     * @template Response - The type of the expected response.
     * @param endpoint - The API endpoint to send the request to (without the base URL).
     * @param displayErrors - Whether to display error modals to the user (default: true).
     * @param params - Additional params for the request. Optional, default to empty object.
     * @returns An Observable of UnitResult<Response> that will emit either:
     *          - A success result containing the response data, or
     *          - A failure result containing error information.
     */
    get<Response>(
        endpoint: string,
        displayErrors: boolean = true,
        params: GetParams = {}
    ): Observable<UnitResult<Response>> {
        const url = `${this.environment.apiUrl}${endpoint}`;
        return this.http.get<Response>(url, { params: params }).pipe(
            map(response => this.mapSuccessResponse<Response>(response)),
            catchError(error => {
                const context: ErrorContext = {
                    url: url,
                    requestData: params,
                    errorResponse: error
                };

                return this.catchError<Response>(context, displayErrors);
            })
        );
    }

    /**
     * Handles HTTP errors from API requests.
     *
     * This method:
     * 1. Maps the HTTP error to a UnitResult using LaravelErrorMapperService.
     * 2. Optionally displays error modals to the user if displayErrors is true.
     * 3. Returns an Observable that emits the error result.
     *
     * @template Response - The type of the expected response.
     * @param context - The error context containing URL, request data, and error response.
     * @param displayErrors - Whether to display error modals to the user.
     * @returns An Observable of UnitResult<Response> containing the error information.
     */
    protected catchError<Response>(context: ErrorContext, displayErrors: boolean): Observable<UnitResult<Response>> {
        const errorResult = this.errorMapper.mapError<Response>(context.errorResponse);
        if (displayErrors) {
            this.errorService.showTypedErrorsModal(errorResult.getErrors()).then();
        }
        return of(errorResult);
    }

    /**
     * Maps a successful API response to a UnitResult object.
     *
     * This method handles different response formats:
     * 1. If the response is null or undefined, returns a success result with undefined.
     * 2. If the response is not an object (e.g., a string or number), returns it directly.
     * 3. If the response follows the David API format (with 'success' and 'data'/'error' properties),
     *    processes it using DavidResponseProcessor.
     * 4. Otherwise, returns the response as-is wrapped in a success UnitResult.
     *
     * @template Response - The type of the expected response.
     * @param response - The raw API response to map.
     * @returns A UnitResult<Response> containing the processed response data.
     */
    private mapSuccessResponse<Response>(response: unknown): UnitResult<Response> {
        if (!response) {
            return UnitResult.success<Response>(undefined as Response);
        }
        if (typeof response !== 'object') {
            return UnitResult.success<Response>(response as Response);
        }

        if ('success' in response && ('data' in response || 'error' in response)) {
            return this.davidResponseProcessor.processResponse<Response>(response as ApiDataType);
        }

        return UnitResult.success<Response>(response as Response);
    }
}
